import {objectFromEntries, range} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {navigate} from 'BenchlingUtil';
import {ResidueType} from 'biojs/BioConstants';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {PolymerSubtype} from 'constants/MolbioConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FileStates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {ActionOrigin, SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {shuffledPastelColors} from 'lib/Colors';
import {
  AssemblyFragmentPolymerCurrentBasicContentsQuery,
  AssemblyFragmentPolymerCurrentBasicContentsQueryVariables,
  AssemblyFragmentPolymerCurrentBasicContentsQuery_polymer_ProteinNode as ProteinCurrentBasicContentsGQL,
  AssemblyFragmentPolymerCurrentBasicContentsQuery_polymer_SequenceNode as SequenceCurrentBasicContentsGQL,
} from 'molbio/bulk-assembly/__generated/AssemblyFragmentPolymerCurrentBasicContentsQuery';
import {
  BasicPolymerInfo,
  BULK_ASSEMBLY_TOPOLOGY_TO_SEQUENCE_TOPOLOGY,
  BulkAssemblyBin,
  BulkAssemblyBinType,
  BulkAssemblyFragmentBin,
  BulkAssemblyMethod,
  BulkAssemblyProcedure,
  BulkAssemblyType,
  Endpoints,
  FALLBACK_ORIENTATION,
  FALLBACK_VISUALIZATION_COLOR,
  FragmentProductionMethod,
  isBulkAssemblyFragmentBin,
  MethodData,
  NormalizedFragment,
  PreviousTool,
  SettingsModalBulkAssemblyParams,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {createAssembly} from 'molbio/bulk-assembly/BulkAssemblyDAO';
import {ASSEMBLY_FRAGMENT_POLYMER_CURRENT_BASIC_CONTENTS_QUERY} from 'molbio/bulk-assembly/BulkAssemblyPolymerData';
import {
  BA_EMPTY_VALUE,
  baId,
  BAIdValue,
  baNumericId,
  BANumericIdValue,
  baOrientation,
  BARawTextValue,
  BulkAssemblyOrientation,
  isBAId,
  isBANumber,
  isBANumericId,
  isEmptyBAValue,
} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {FragmentModalFragment} from 'molbio/bulk-assembly/polymer-map-modals/FragmentCardContents';
import {CloningSetupModal, CloningSetupModalProps} from 'molbio/bulk-assembly/setup-modal/CloningSetupModal';
import {
  ConcatenationSetupModal,
  ConcatenationSetupModalProps,
} from 'molbio/bulk-assembly/setup-modal/ConcatenationSetupModal';
import {getPreferredRestrictionEnzymeId} from 'molbio/bulk-assembly/util/EnzymeUtil';
import {
  getAutopopulatedFragmentEndpoints,
  getAutopopulatedRestrictionEnzymes,
} from 'molbio/bulk-assembly/util/FragmentEndpointUtil';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';

export async function handleNewCloningBulkAssemblyClick(
  propsOverride: Partial<CloningSetupModalProps>,
  actionOrigin: ActionOrigin
): Promise<void> {
  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.INITIATED_BULK_ASSEMBLY, {
    actionOrigin,
    bulkAssemblyType: BulkAssemblyType.CLONING,
  });
  const newAssemblyParams = await runModal(CloningSetupModal, {
    canEditAssembly: true,
    canEditFolder: true,
    canEditName: true,
    isInitialSetup: true,
    actionOrigin,
    ...propsOverride,
  });
  await handleCreateNewBulkAssembly(newAssemblyParams, actionOrigin);
}

export function handleOpenBulkAssembleDNA(): void {
  if (BenchlingConfig.MOLBIO_REDESIGNED_CLONING_FLOW) {
    handleNewCloningBulkAssemblyClick(
      {previousTool: PreviousTool.LEGACY_BULK_ASSEMBLY},
      ActionOrigin.MOLBIO_BULK_ASSEMBLY
    );
  } else {
    navigate(null, '/bulk-workflows/assembly');
  }
}

export async function handleNewConcatenationBulkAssemblyClick(
  propsOverride: Partial<ConcatenationSetupModalProps>,
  actionOrigin: ActionOrigin
): Promise<void> {
  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.INITIATED_BULK_ASSEMBLY, {
    actionOrigin,
    bulkAssemblyType: BulkAssemblyType.CONCATENATION,
  });
  const newAssemblyParams = await runModal(ConcatenationSetupModal, {
    canEditAssembly: true,
    canEditFolder: true,
    canEditName: true,
    isInitialSetup: true,
    actionOrigin,
    ...propsOverride,
  });
  await handleCreateNewBulkAssembly(newAssemblyParams, actionOrigin);
}

async function handleCreateNewBulkAssembly(
  newAssemblyParams: SettingsModalBulkAssemblyParams | null,
  actionOrigin: ActionOrigin
): Promise<void> {
  if (newAssemblyParams != null) {
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, newAssemblyParams.folderId);
    await withLoadingScreen(
      createAssembly(newAssemblyParams).then((bulkAssembly) => {
        FileStates.get().openFolderItem(bulkAssembly.id, {open: true});
        FolderDAOCache.invalidateFolderContents(newAssemblyParams.folderId);
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.SAVED_BULK_ASSEMBLY_SETTINGS, {
          actionOrigin,
          topology: BULK_ASSEMBLY_TOPOLOGY_TO_SEQUENCE_TOPOLOGY[newAssemblyParams.procedureJson.topology],
          parameters: newAssemblyParams.procedureJson.method,
          assemblyId: bulkAssembly.id,
        });
      })
    );
  }
}

/**
 * A typesafe function for performing a cartesian product of all leaf elements in an array of arrays.
 *
 * For example, cartesianProduct([[1, 2], [3, 4, 5]]) returns:
 * [
 *   [1, 3],
 *   [1, 4],
 *   [1, 5],
 *   [2, 3],
 *   [2, 4],
 *   [2, 5],
 * ]
 *
 * @param {T[][]} lists A list of lists of T.
 */
export function cartesianProduct<T>(lists: Array<Array<T>>): Array<Array<T>> {
  return lists.reduce<Array<Array<T>>>(
    // For each list in lists,
    (acc: Array<Array<T>>, list: Array<T>) =>
      // For each result-list we have already,
      acc.flatMap((resultList: Array<T>) =>
        // make a new set of result-lists that append each element of list onto the existing resultList
        list.map((listElement: T) => [...resultList, listElement])
      ), // and then flatten them into acc.
    [[]]
  );
}

export function createEmptyFragmentBins(
  emptyFragmentBinCount: number,
  fragmentProductionMethod: FragmentProductionMethod,
  topology: SequenceTopology,
  assemblyMethod: BulkAssemblyMethod
): Array<BulkAssemblyBin> {
  if (topology === SequenceTopology.CIRCULAR && assemblyMethod !== BulkAssemblyMethod.CONCATENATION) {
    const backboneBin: BulkAssemblyFragmentBin = {
      cid: uuidv4(),
      name: 'Backbone',
      color: shuffledPastelColors[0],
      binType: BulkAssemblyBinType.FRAGMENT,
      fragmentProductionMethod,
    };
    const insertBins: Array<BulkAssemblyFragmentBin> = range(1, emptyFragmentBinCount - 1).map((index) => ({
      cid: uuidv4(),
      name: `Insert ${index}`,
      color: shuffledPastelColors[index],
      binType: BulkAssemblyBinType.FRAGMENT,
      fragmentProductionMethod,
    }));
    return [backboneBin, ...insertBins];
  }
  return range(1, emptyFragmentBinCount).map((index) => ({
    cid: uuidv4(),
    name: `${assemblyMethod === BulkAssemblyMethod.CONCATENATION ? 'Bin' : 'Insert'} ${index}`,
    color: shuffledPastelColors[index - 1],
    binType: BulkAssemblyBinType.FRAGMENT,
    fragmentProductionMethod,
  }));
}

export function makeProcedureBinFields(
  bins: Array<BulkAssemblyBin>
): Pick<BulkAssemblyProcedure, 'binCidOrdering' | 'binsByCid'> {
  return {
    binCidOrdering: bins.map((bin) => bin.cid),
    binsByCid: objectFromEntries(bins.map((bin) => [bin.cid, bin])),
  };
}

export function fragmentDataToNormalizedFragment(fragmentData: FragmentModalFragment): NormalizedFragment {
  if (APIUtil.isNucleotideSequence(fragmentData.polymerId)) {
    return {
      ...fragmentData.endpoints,
      cid: fragmentData.cid ?? uuidv4(),
      sequence: baId(fragmentData.polymerId),
      sequenceVersionDbId: fragmentData.versionDbId,
      protein: BA_EMPTY_VALUE,
      proteinVersionDbId: null,
      orientation: baOrientation(fragmentData.orientation),
      bin: fragmentData.binCid != null ? baId(fragmentData.binCid) : BA_EMPTY_VALUE,
      preferredPrimer5:
        fragmentData.preferredPrimer5Id != null ? baId(fragmentData.preferredPrimer5Id) : BA_EMPTY_VALUE,
      preferredPrimer3:
        fragmentData.preferredPrimer3Id != null ? baId(fragmentData.preferredPrimer3Id) : BA_EMPTY_VALUE,
      restrictionEnzyme5:
        fragmentData.restrictionEnzymeId5 != null
          ? baNumericId(fragmentData.restrictionEnzymeId5)
          : BA_EMPTY_VALUE,
      restrictionEnzyme3:
        fragmentData.restrictionEnzymeId3 != null
          ? baNumericId(fragmentData.restrictionEnzymeId3)
          : BA_EMPTY_VALUE,
    };
  } else {
    return {
      ...fragmentData.endpoints,
      cid: fragmentData.cid ?? uuidv4(),
      sequence: BA_EMPTY_VALUE,
      sequenceVersionDbId: null,
      protein: baId(fragmentData.polymerId),
      proteinVersionDbId: fragmentData.versionDbId,
      orientation: baOrientation(fragmentData.orientation),
      bin: fragmentData.binCid != null ? baId(fragmentData.binCid) : BA_EMPTY_VALUE,
      preferredPrimer5: BA_EMPTY_VALUE,
      preferredPrimer3: BA_EMPTY_VALUE,
      restrictionEnzyme5: BA_EMPTY_VALUE,
      restrictionEnzyme3: BA_EMPTY_VALUE,
    };
  }
}

export function polymerInfoFromGQL(
  polymer: ProteinCurrentBasicContentsGQL | SequenceCurrentBasicContentsGQL
): BasicPolymerInfo {
  if (polymer.__typename === 'SequenceNode') {
    return {
      residues: polymer.experimentalCurrentVersion.bases,
      circular: polymer.experimentalCurrentVersion.circular,
      startIndex: polymer.experimentalCurrentVersion.startIndex,
      singleStranded: polymer.sequenceType !== SequenceType.ds,
      residueType: ResidueType.NUCLEOTIDE,
    };
  } else {
    return {
      residues: polymer.experimentalCurrentVersion.aminoAcids,
      circular: false,
      startIndex: 1,
      singleStranded: true,
      residueType: ResidueType.AA,
    };
  }
}

async function fetchPolymerDetails(
  polymerId: string
): Promise<{latestVersionDbId: number; basicPolymerInfo: BasicPolymerInfo}> {
  const queryResult = await doQuerySafe<
    AssemblyFragmentPolymerCurrentBasicContentsQuery,
    AssemblyFragmentPolymerCurrentBasicContentsQueryVariables
  >(
    {query: ASSEMBLY_FRAGMENT_POLYMER_CURRENT_BASIC_CONTENTS_QUERY},
    {variables: {id: polymerId}, fetchPolicy: 'network-only'}
  );
  if (isInaccessible(queryResult.polymer)) {
    throw new Error(`Polymer ${polymerId} is inaccessible`);
  } else {
    return {
      basicPolymerInfo: polymerInfoFromGQL(queryResult.polymer),
      latestVersionDbId: queryResult.polymer.experimentalCurrentVersion.dbId,
    };
  }
}

/**
 * Fetch information from the server about a specific fragment to be viewed, and autofill start
 * and end if applicable.
 *
 * Currently, this function always loads the *latest* version of the sequence, even if the user
 * was working with an older version. If we're updating from an old version, return a flag so
 * that we can show a warning to the user, and re-autofill start and end.
 */
export async function constructFragmentData({
  polymerId,
  polymerKind,
  expectedVersionDbId,
  showPrimerSearch,
  endpoints = {start: BA_EMPTY_VALUE, end: BA_EMPTY_VALUE},
  color = FALLBACK_VISUALIZATION_COLOR,
  orientation = FALLBACK_ORIENTATION,
  binName = null,
  preferredPrimer5Id = null,
  preferredPrimer3Id = null,
  cid = null,
  binCid = null,
  restrictionEnzyme5 = BA_EMPTY_VALUE,
  restrictionEnzyme3 = BA_EMPTY_VALUE,
  canSpecifyEndpoints,
  canUpdateRestrictionEnzymes,
  binIndex,
  constructFragmentIndex,
  numFragmentBins,
  constructTopology,
  assemblyMethod,
  fragmentProductionMethod,
}: {
  polymerId: string;
  polymerKind: 'sequence' | 'protein';
  expectedVersionDbId: number | null;
  showPrimerSearch: boolean;
  endpoints?: Endpoints;
  color?: string;
  orientation?: BulkAssemblyOrientation;
  binName?: string | null;
  preferredPrimer5Id?: string | null;
  preferredPrimer3Id?: string | null;
  cid?: string | null;
  binCid?: string | null;
  binIndex?: number | null;
  constructFragmentIndex: number | null;
  numFragmentBins: number;
  restrictionEnzyme5?: BANumericIdValue | BARawTextValue;
  restrictionEnzyme3?: BANumericIdValue | BARawTextValue;
  canSpecifyEndpoints: boolean;
  canUpdateRestrictionEnzymes: boolean;

  constructTopology: SequenceTopology;
  assemblyMethod: MethodData;
  fragmentProductionMethod: FragmentProductionMethod;
}): Promise<FragmentModalFragment> {
  const {latestVersionDbId, basicPolymerInfo} = await fetchPolymerDetails(polymerId);
  const hadStaleVersion = expectedVersionDbId != null && expectedVersionDbId !== latestVersionDbId;
  let autopopulatedRestrictionEnzymes: {
    restrictionEnzyme5: BANumericIdValue | BARawTextValue;
    restrictionEnzyme3: BANumericIdValue | BARawTextValue;
  } = {restrictionEnzyme5, restrictionEnzyme3};
  if (
    (hadStaleVersion || (isEmptyBAValue(restrictionEnzyme5) && isEmptyBAValue(restrictionEnzyme3))) &&
    binIndex != null &&
    polymerKind === 'sequence'
  ) {
    const maybeAutopopulatedRestrictionEnzymes = getAutopopulatedRestrictionEnzymes(
      basicPolymerInfo,
      getPreferredRestrictionEnzymeId(assemblyMethod),
      binIndex,
      constructTopology,
      numFragmentBins,
      fragmentProductionMethod
    );
    if (maybeAutopopulatedRestrictionEnzymes != null) {
      autopopulatedRestrictionEnzymes = maybeAutopopulatedRestrictionEnzymes;
    }
  }
  if (hadStaleVersion || (isEmptyBAValue(endpoints.start) && isEmptyBAValue(endpoints.end))) {
    const newEndpoints = getAutopopulatedFragmentEndpoints(
      basicPolymerInfo,
      canSpecifyEndpoints,
      autopopulatedRestrictionEnzymes.restrictionEnzyme5,
      autopopulatedRestrictionEnzymes.restrictionEnzyme3,
      hadStaleVersion ? {start: BA_EMPTY_VALUE, end: BA_EMPTY_VALUE} : endpoints
    );
    // If overwriting endpoints, then set orientation to the default (forward).
    if (newEndpoints != null && isBANumber(newEndpoints.start)) {
      endpoints = newEndpoints;
      orientation = FALLBACK_ORIENTATION;
    }
  }

  return {
    endpoints,
    versionDbId: latestVersionDbId,
    polymerId,
    hadStaleVersion,
    color,
    orientation,
    binName,
    showPrimerSearch,
    preferredPrimer5Id,
    preferredPrimer3Id,
    cid,
    binCid,
    restrictionEnzymeId5: isBANumericId(autopopulatedRestrictionEnzymes.restrictionEnzyme5)
      ? autopopulatedRestrictionEnzymes.restrictionEnzyme5.id
      : null,
    restrictionEnzymeId3: isBANumericId(autopopulatedRestrictionEnzymes.restrictionEnzyme3)
      ? autopopulatedRestrictionEnzymes.restrictionEnzyme3.id
      : null,
    fragmentProductionMethod,
    basicPolymerInfo,
    canUpdateRestrictionEnzymes,
    constructFragmentIndex,
  };
}

export function methodCanSpecifyEndpoints(method: BulkAssemblyMethod): boolean {
  return method !== BulkAssemblyMethod.HOMOLOGY;
}

export function isProteinBasedAssembly(methodData: MethodData): boolean {
  return (
    methodData.type === BulkAssemblyMethod.CONCATENATION &&
    methodData.parameters.polymerSubtype === PolymerSubtype.aa_sequence
  );
}

export function polymerDataFromFragment(fragment: {
  sequence: BAIdValue | BARawTextValue;
  sequenceVersionDbId: number | null;
  protein: BAIdValue | BARawTextValue;
  proteinVersionDbId: number | null;
}): {
  polymer: BAIdValue | BARawTextValue;
  polymerVersionDbId: number | null;
  polymerKind: 'sequence' | 'protein';
} {
  if (isBAId(fragment.protein) || !isEmptyBAValue(fragment.protein)) {
    return {
      polymerKind: 'protein',
      polymer: fragment.protein,
      polymerVersionDbId: fragment.proteinVersionDbId,
    };
  } else {
    return {
      polymerKind: 'sequence',
      polymer: fragment.sequence,
      polymerVersionDbId: fragment.sequenceVersionDbId,
    };
  }
}

export function generateNewBinName(
  existingBins: Array<BulkAssemblyBin>,
  assemblyMethod: BulkAssemblyMethod
): string {
  // Name the new bin with a number one higher than the current highest untitled bin index
  const highestUntitledBinIndex = Math.max(
    ...existingBins.map((bin) => {
      const match = bin.name.match(/^((untitled bin)|(insert)|(bin)) (?<binIndex>\d+)$/i);
      if (match == null) {
        return 0;
      } else {
        return Number.parseInt(match.groups!.binIndex, 10);
      }
    })
  );
  return `${assemblyMethod === BulkAssemblyMethod.CONCATENATION ? 'Bin' : 'Insert'} ${
    highestUntitledBinIndex + 1
  }`;
}

export function getNewBinColor(existingBins: Array<BulkAssemblyBin>): string {
  const fragmentBins = existingBins.filter((bin) => isBulkAssemblyFragmentBin(bin));
  // Get the next color that is not already used by an existing bin when possible.
  // If all colors have been used, choose one that differs from the first and last bin colors,
  // since the new bin will be added as the last bin.
  const existingColors = new Set(fragmentBins.map((bin) => bin.color));
  const colorsToAvoid =
    existingColors.size < shuffledPastelColors.length
      ? existingColors
      : new Set([fragmentBins[fragmentBins.length - 1].color, fragmentBins[0].color]);
  return shuffledPastelColors.find((color) => !colorsToAvoid.has(color))!;
}
